import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { announce } from '../../app/live/LiveRegion';
import { useReducedMotion } from '../a11y/useReducedMotion';
import type { IconName } from '../Icon';
import { ToastCard, type ToastAction, type ToastTone } from './ToastCard';
import styles from './Toast.module.css';

// Toast stack — DESIGN_SYSTEM §12 row 25: bottom-left on web, newest at the bottom, 6 s (10 s when it carries
// an action such as Undo), the countdown pauses while the pointer or the focus is on the stack. Every toast is
// ANNOUNCED through the app's one aria-live region (`announce()` — polite; an error interrupts, assertive), so
// the cards themselves are not live regions and nothing is spoken twice. `useToast()` is the API; the
// provider sits in `AppProviders`.

export const TOAST_DURATION_MS = 6_000;
export const TOAST_WITH_ACTION_DURATION_MS = 10_000;

export interface ToastOptions {
  /** What the card shows; may carry `<strong>` for the lead. */
  message: ReactNode;
  /** What the live region says. Required when `message` is not a plain string. */
  announcement?: string;
  tone?: ToastTone;
  icon?: IconName;
  action?: ToastAction;
  /** ms; default 6 000, or 10 000 with an action. `0` keeps it until dismissed. */
  duration?: number;
  /** Test id of the card. */
  testID?: string;
  /** A toast with the same key replaces the one still showing (e.g. repeated refresh failures). */
  key?: string;
}

export interface ToastApi {
  /** Shows a toast and announces it; returns its id. */
  toast: (options: ToastOptions) => string;
  dismiss: (id: string) => void;
  dismissAll: () => void;
}

interface ToastRecord extends ToastOptions {
  id: string;
  duration: number;
}

const ToastContext = createContext<ToastApi | null>(null);
let seq = 0;

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) {
    throw new Error('useToast() needs a <ToastProvider> above it (AppProviders has one).');
  }
  return api;
}

function announcementFor(options: ToastOptions): string {
  if (options.announcement !== undefined) return options.announcement;
  if (typeof options.message === 'string') return options.message;
  if (import.meta.env.DEV) {
    throw new Error(
      'A toast whose message is not a plain string needs an `announcement` for the live region.',
    );
  }
  return '';
}

/** Counts down `duration` ms and calls `onExpire`, holding still while `paused`. */
function useCountdown(duration: number, paused: boolean, onExpire: () => void) {
  const remaining = useRef(duration);
  const expire = useRef(onExpire);
  expire.current = onExpire;
  useEffect(() => {
    if (duration <= 0 || paused) return;
    const startedAt = performance.now();
    const timer = setTimeout(() => expire.current(), remaining.current);
    return () => {
      clearTimeout(timer);
      remaining.current = Math.max(0, remaining.current - (performance.now() - startedAt));
    };
  }, [duration, paused]);
}

function ToastItem({
  toast,
  paused,
  showTimer,
  onDismiss,
}: {
  toast: ToastRecord;
  paused: boolean;
  showTimer: boolean;
  onDismiss: () => void;
}) {
  useCountdown(toast.duration, paused, onDismiss);
  const action = toast.action
    ? {
        ...toast.action,
        onClick: () => {
          toast.action?.onClick();
          onDismiss();
        },
      }
    : undefined;
  return (
    <li className={styles.item}>
      <ToastCard
        message={toast.message}
        tone={toast.tone}
        icon={toast.icon}
        action={action}
        onDismiss={onDismiss}
        duration={toast.duration}
        paused={paused}
        showTimer={showTimer}
        testID={toast.testID}
      />
    </li>
  );
}

export function ToastProvider({
  children,
  regionLabel = 'Notifications',
}: {
  children: ReactNode;
  regionLabel?: string;
}) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const reducedMotion = useReducedMotion();

  const dismiss = useCallback((id: string) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);
  const dismissAll = useCallback(() => setToasts([]), []);
  const toast = useCallback((options: ToastOptions): string => {
    const text = announcementFor(options);
    const id = `toast-${++seq}`;
    const duration =
      options.duration ?? (options.action ? TOAST_WITH_ACTION_DURATION_MS : TOAST_DURATION_MS);
    setToasts((list) => [
      ...list.filter((t) => options.key === undefined || t.key !== options.key),
      { ...options, id, duration },
    ]);
    announce(text, options.tone === 'error' ? 'assertive' : 'polite');
    return id;
  }, []);
  const api = useMemo<ToastApi>(
    () => ({ toast, dismiss, dismissAll }),
    [toast, dismiss, dismissAll],
  );
  const paused = hovered || focused;

  return (
    <ToastContext.Provider value={api}>
      {children}
      {toasts.length > 0
        ? createPortal(
            <ul
              className={styles.stack}
              aria-label={regionLabel}
              data-testid="toast.stack"
              onPointerEnter={() => setHovered(true)}
              onPointerLeave={() => setHovered(false)}
              onFocus={() => setFocused(true)}
              onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget as Node | null))
                  setFocused(false);
              }}
            >
              {toasts.map((t) => (
                <ToastItem
                  key={t.id}
                  toast={t}
                  paused={paused}
                  showTimer={!reducedMotion}
                  onDismiss={() => dismiss(t.id)}
                />
              ))}
            </ul>,
            document.body,
          )
        : null}
    </ToastContext.Provider>
  );
}
